// Pure client-side projection of a lift's next PPM service date from its last-serviced date, used by
// the Lifts register's "Last serviced" editor to show the next service due as the office edits the
// date (Session P, Item 4). It MIRRORS the server generator (server/src/modules/scheduling/
// ppmGenerate.ts computePpmDueDates): the cadence steps by the frequency, only the first occurrence
// strictly after today counts, and a weekend due date is pushed to the following Monday. So the date
// the modal shows is the date the generator will schedule.

// Months between visits per frequency. Mirrors server FREQUENCY_MONTHS (kept in step deliberately).
export const FREQUENCY_MONTHS: Record<string, number> = {
  monthly: 1,
  bi_monthly: 2,
  quarterly: 3,
  six_monthly: 6,
  annual: 12,
};

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfUtcDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

// Add whole months in UTC, clamping the day so the 31st of a month never overflows into the next
// month (matches the server's addMonthsUtc).
function addMonthsUtc(date: Date, months: number): Date {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth() + months;
  const targetY = y + Math.floor(m / 12);
  const targetM = ((m % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(targetY, targetM + 1, 0)).getUTCDate();
  const day = Math.min(date.getUTCDate(), lastDay);
  return new Date(Date.UTC(targetY, targetM, day, date.getUTCHours(), date.getUTCMinutes(), 0, 0));
}

// Move a Saturday/Sunday due date to the following Monday (ELE works weekdays only).
function pushToNextWorkingDay(d: Date): Date {
  const day = d.getUTCDay(); // 0 = Sunday, 6 = Saturday
  const add = day === 6 ? 2 : day === 0 ? 1 : 0;
  return add === 0 ? d : new Date(d.getTime() + add * DAY_MS);
}

// The next PPM service date projected from `lastServicedAt`, at 09:00 UTC (the generator's slot),
// weekend-pushed. Returns null for a missing/invalid date or an unknown frequency.
//
// This is the FIRST occurrence whose day is strictly after the anchor's day -- i.e. anchor + N
// intervals for the smallest N (always N = 1 for a positive cadence). It is returned EVEN WHEN IT IS
// IN THE PAST: an overdue visit must be surfaced (the caller flags it red / "Overdue"), never hidden
// by skipping to the next future occurrence. This is the bug Session Q fixed -- the old code stepped
// past any date on or before today, so a lift one cycle overdue showed its NEXT-but-one date and the
// overdue visit vanished from the register.
//
// It still mirrors the server generator's interval, day clamp and weekend push, but NOT the
// generator's future-only filter: the generator must never schedule a job in the past; the display
// must never hide one. (Generator: server/src/modules/scheduling/ppmGenerate.ts computePpmDueDates.)
export function projectNextServiceDue(
  lastServicedAt: Date | string | null | undefined,
  frequency: string | null | undefined,
): Date | null {
  if (lastServicedAt == null) return null;
  const anchor = lastServicedAt instanceof Date ? lastServicedAt : new Date(lastServicedAt);
  if (Number.isNaN(anchor.getTime())) return null;
  const step = frequency ? FREQUENCY_MONTHS[frequency] : undefined;
  if (step == null || step <= 0) return null;

  const anchorDay = startOfUtcDay(anchor);
  // Step forward from the anchor until the occurrence's day is strictly after the ANCHOR's day -- the
  // smallest N that puts the date after the anchor (always the first interval; cap is a backstop).
  for (let k = 1; k <= 600; k++) {
    const occ = addMonthsUtc(anchor, k * step);
    if (startOfUtcDay(occ) <= anchorDay) continue;
    const at9 = new Date(Date.UTC(occ.getUTCFullYear(), occ.getUTCMonth(), occ.getUTCDate(), 9, 0, 0, 0));
    return pushToNextWorkingDay(at9);
  }
  return null;
}

// The display band for a projected service-due date, relative to `now`: "overdue" (before today),
// "due_soon" (today up to 30 days ahead) or "scheduled" (more than 30 days ahead). Returns null for a
// missing/invalid date. Pure and date-only -- the colour classes and labels live in the component
// that renders it (LastServicedButton), so this stays presentation-free and easy to test.
export type ServiceDueTone = "overdue" | "due_soon" | "scheduled";

export function classifyServiceDue(due: Date | null | undefined, now: Date = new Date()): ServiceDueTone | null {
  if (due == null || Number.isNaN(due.getTime())) return null;
  const today = startOfUtcDay(now);
  const dueDay = startOfUtcDay(due);
  if (dueDay < today) return "overdue";
  if (dueDay - today <= 30 * DAY_MS) return "due_soon";
  return "scheduled";
}
